var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAPI } from '../../../state';
import { TABLE_PREFS_API, useUserSessionAPI } from '../../app/selectors';
var mergeWithDefaults = function (saved, defaultFields, defaultHidden) {
  var newFields = defaultFields.filter(function (f) {
    return !saved.order.includes(f);
  });
  var newHidden = newFields.filter(function (f) {
    return defaultHidden.includes(f);
  });
  return {
    order: __spreadArray(__spreadArray([], saved.order, true), newFields, true),
    hidden: __spreadArray(__spreadArray([], saved.hidden, true), newHidden, true)
  };
};
export var useColumnPreferences = function (tableKey, defaultFields, defaultHidden) {
  if (defaultHidden === void 0) {
    defaultHidden = [];
  }
  var sessionData = useUserSessionAPI()[0].data;
  var _a = useAPI(TABLE_PREFS_API, {}, 'PUT'),
    saveError = _a[0].error,
    savePrefs = _a[1];
  // Refs keep the hook stable even when callers pass inline arrays or change tableKey
  var tableKeyRef = useRef(tableKey);
  tableKeyRef.current = tableKey;
  var defaultFieldsRef = useRef(defaultFields);
  defaultFieldsRef.current = defaultFields;
  var defaultHiddenRef = useRef(defaultHidden);
  defaultHiddenRef.current = defaultHidden;
  var defaultPrefsRef = useRef({
    order: defaultFields,
    hidden: defaultHidden
  });
  useEffect(function () {
    defaultPrefsRef.current = {
      order: defaultFields,
      hidden: defaultHidden
    };
  }, [defaultFields, defaultHidden]);
  var _b = useState(function () {
      var _a;
      var saved = (_a = sessionData === null || sessionData === void 0 ? void 0 : sessionData.preferences) === null || _a === void 0 ? void 0 : _a[tableKey];
      return saved ? mergeWithDefaults(saved, defaultFields, defaultHidden) : defaultPrefsRef.current;
    }),
    prefs = _b[0],
    setPrefs = _b[1];
  var prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  // Tracks the last committed prefs so a failed save can be rolled back
  var prefsBeforeSaveRef = useRef(defaultPrefsRef.current);
  var _c = useState(!!sessionData),
    initialized = _c[0],
    setInitialized = _c[1];
  useEffect(function () {
    var _a;
    if (!initialized && sessionData) {
      var saved = (_a = sessionData.preferences) === null || _a === void 0 ? void 0 : _a[tableKeyRef.current];
      setPrefs(saved ? mergeWithDefaults(saved, defaultFieldsRef.current, defaultHiddenRef.current) : defaultPrefsRef.current);
      setInitialized(true);
    }
  }, [sessionData, initialized]);
  useEffect(function () {
    if (saveError) {
      setPrefs(prefsBeforeSaveRef.current);
    }
  }, [saveError]);
  var update = useCallback(function (order, hidden) {
    prefsBeforeSaveRef.current = prefsRef.current;
    var newPrefs = {
      order: order,
      hidden: hidden
    };
    setPrefs(newPrefs);
    savePrefs(__assign({
      tableKey: tableKeyRef.current
    }, newPrefs));
  }, [savePrefs]);
  var reset = useCallback(function () {
    prefsBeforeSaveRef.current = prefsRef.current;
    setPrefs(defaultPrefsRef.current);
    savePrefs(__assign({
      tableKey: tableKeyRef.current
    }, defaultPrefsRef.current));
  }, [savePrefs]);
  return {
    prefs: prefs,
    update: update,
    reset: reset
  };
};